import React, { useEffect, useState } from "react";
import { ExternalLink, Save, Trash2, ImageIcon, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import NativeSelect from "@/components/forms/NativeSelect";
import ImageUploader, { resolveImageUrl } from "@/components/forms/ImageUploader";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * AdminHomeBanner — full CRUD-lite panel for the singleton homepage banner.
 *
 * The banner is stored as a single MongoDB document (id="home_top") and the
 * endpoints upsert / delete it. Admins can:
 *   - Upload a new banner image (client-side capped at 1 MB, WebP/JPG/PNG).
 *   - Set an optional destination URL.
 *   - Choose whether the link opens in the same tab or a new tab.
 *   - Toggle the banner on/off without deleting it.
 *   - Preview the banner before saving (uses the same rounded-2xl frame as
 *     the public site so what admins see is exactly what visitors will see).
 *   - Remove the banner entirely.
 *
 * "Save" is only enabled after the admin makes edits — this avoids accidental
 * PUTs that would refresh `updated_at` without a real change.
 */
const BANNER_MAX_BYTES = 1 * 1024 * 1024; // 1 MB (per spec)

export default function AdminHomeBanner() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Local form state
  const [imageUrl, setImageUrl] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [target, setTarget] = useState("_self");
  const [altText, setAltText] = useState("");
  const [enabled, setEnabled] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/banners/home");
      const b = data.banner;
      if (b) {
        setImageUrl(b.image_url || "");
        setLinkUrl(b.link_url || "");
        setTarget(b.target || "_self");
        setAltText(b.alt_text || "");
        setEnabled(b.enabled !== false);
      } else {
        // Reset to blank slate
        setImageUrl("");
        setLinkUrl("");
        setTarget("_self");
        setAltText("");
        setEnabled(true);
      }
      setDirty(false);
    } catch (e) {
      toast.error("Failed to load banner settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const mark = (setter) => (val) => { setter(val); setDirty(true); };

  const save = async () => {
    if (!imageUrl) {
      toast.error("Please upload a banner image first.");
      return;
    }
    setSaving(true);
    try {
      await api.put("/admin/banners/home", {
        image_url: imageUrl,
        link_url: linkUrl || null,
        target,
        alt_text: altText || null,
        enabled,
      });
      toast.success("Banner saved successfully.");
      setDirty(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to save banner.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove the homepage banner entirely? The section will be hidden from visitors.")) return;
    setRemoving(true);
    try {
      await api.delete("/admin/banners/home");
      toast.success("Banner removed.");
      await load();
    } catch (e) {
      toast.error("Failed to remove banner.");
    } finally {
      setRemoving(false);
    }
  };

  const previewSrc = imageUrl ? resolveImageUrl(imageUrl) : "";

  return (
    <div data-testid="admin-home-banner-page" className="space-y-6">
      {/* Header */}
      <div>
        <div className="text-xs uppercase tracking-widest text-[#7A5A22]">Homepage</div>
        <h1 className="font-heading text-2xl sm:text-3xl text-[#111827] mt-1">Advertisement Banner</h1>
        <p className="text-sm text-[#4B5563] mt-1">
          Manage the promotional banner that appears between the Premium Spas
          carousel and the Popular Areas section.
        </p>
      </div>

      {/* Spec card */}
      <div className="rounded-2xl bg-[#F5EFE6] border border-[#E7DDCF] p-4 flex items-start gap-3">
        <Info size={16} className="text-[#7A5A22] mt-0.5 shrink-0" />
        <div className="text-xs text-[#4B5563] leading-relaxed">
          <div className="font-medium text-[#1F2937] mb-1">Recommended banner specs</div>
          Dimensions <strong>1440 × 300 px</strong> (aspect ratio 4.8 : 1). High-res: 2880 × 600 px.
          Formats: <strong>WebP (preferred)</strong>, JPG, PNG. Max file size <strong>1 MB</strong>.
          The image is auto-cropped with <em>object-cover</em>, so keep important content within the central safe zone.
        </div>
      </div>

      {/* Form */}
      <div className="rounded-2xl bg-white border border-[#E7DDCF] p-5 sm:p-6 space-y-5">
        {/* Image upload */}
        <div>
          <Label className="text-xs">Banner Image</Label>
          <div className="mt-2">
            <ImageUploader
              value={imageUrl}
              onChange={(url) => { setImageUrl(url || ""); setDirty(true); }}
              multiple={false}
              maxBytesPerImage={BANNER_MAX_BYTES}
              aspectRatio="wide"
              testId="admin-home-banner-uploader"
            />
          </div>
        </div>

        {/* Destination URL */}
        <div>
          <Label className="text-xs" htmlFor="banner-link-url">Destination URL (optional)</Label>
          <Input
            id="banner-link-url"
            value={linkUrl}
            onChange={(e) => mark(setLinkUrl)(e.target.value)}
            placeholder="https://example.com/campaign"
            data-testid="admin-home-banner-link-url"
            className="mt-1"
          />
          <div className="text-xs text-[#4B5563] mt-1">
            Leave blank to render a non-clickable banner. Otherwise the entire banner becomes a link.
          </div>
        </div>

        {/* Target */}
        <div>
          <Label className="text-xs">Open link in</Label>
          <NativeSelect
            value={target}
            onChange={(v) => mark(setTarget)(v)}
            data-testid="admin-home-banner-target"
            className="mt-1"
          >
            <option value="_self">Same Tab</option>
            <option value="_blank">New Tab</option>
          </NativeSelect>
        </div>

        {/* Alt text */}
        <div>
          <Label className="text-xs" htmlFor="banner-alt-text">Alt Text (accessibility)</Label>
          <Input
            id="banner-alt-text"
            value={altText}
            onChange={(e) => mark(setAltText)(e.target.value)}
            placeholder="e.g. Diwali special — 20% off luxury spas"
            data-testid="admin-home-banner-alt-text"
            className="mt-1"
          />
        </div>

        {/* Enable toggle */}
        <div className="flex items-center justify-between rounded-xl bg-[#F5EFE6] border border-[#E7DDCF] px-4 py-3">
          <div>
            <div className="text-sm font-medium text-[#1F2937]">Banner Enabled</div>
            <div className="text-xs text-[#4B5563] mt-0.5">
              When disabled the banner section is hidden from the homepage entirely (no empty space).
            </div>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={(v) => mark(setEnabled)(v)}
            data-testid="admin-home-banner-enabled-toggle"
          />
        </div>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-2">
          <Button
            variant="outline"
            className="gap-2 text-[#B42318] border-[#F1D5D5] hover:bg-[#FEF3F2]"
            onClick={remove}
            disabled={removing || loading}
            data-testid="admin-home-banner-remove-button"
          >
            <Trash2 size={14} /> {removing ? "Removing..." : "Remove Banner"}
          </Button>
          <Button
            className="gap-2 bg-[#1F4E3D] hover:bg-[#2E5D4E] text-white"
            onClick={save}
            disabled={saving || loading || !dirty || !imageUrl}
            data-testid="admin-home-banner-save-button"
          >
            <Save size={14} /> {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>

      {/* Preview */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs uppercase tracking-widest text-[#7A5A22]">Live Preview</Label>
          {linkUrl && (
            <div className="text-xs text-[#4B5563] flex items-center gap-1">
              Links to <span className="font-medium text-[#1F2937]">{linkUrl}</span>
              <ExternalLink size={12} />
            </div>
          )}
        </div>
        <div className="rounded-2xl border border-dashed border-[#E7DDCF] p-4 bg-[#FBF7F0]">
          {previewSrc ? (
            <div
              data-testid="admin-home-banner-preview"
              className={`block rounded-2xl overflow-hidden bg-[#F5EFE6] aspect-[4.8/1] shadow-[0_10px_28px_rgba(17,24,39,0.06)] ${enabled ? "" : "opacity-50"}`}
            >
              <img src={previewSrc} alt={altText || "Preview"} className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="aspect-[4.8/1] rounded-2xl bg-white border border-dashed border-[#E7DDCF] flex flex-col items-center justify-center text-[#4B5563]">
              <ImageIcon size={24} className="mb-2 opacity-60" />
              <div className="text-sm">Upload a banner image to see the preview</div>
            </div>
          )}
          {!enabled && previewSrc && (
            <div className="text-xs text-[#B42318] mt-2 text-center">
              Banner is currently <strong>disabled</strong> — visitors will not see it on the homepage.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
